import React, { useState, useRef, useCallback } from 'react';
import { Loader2, ImagePlus, X, AlertCircle, CheckCircle2, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_BYTES = 12 * 1024 * 1024; // 12MB (matches backend)
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ACCEPTED_EXTS = ['.jpg', '.jpeg', '.png', '.webp'];

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${BACKEND_URL}${u}`;
};

/**
 * Robust image uploader with:
 * - Client-side validation (type + size)
 * - Real per-file upload progress (XHR)
 * - Drag & drop
 * - Per-file error reporting
 * - Live preview thumbnails with onError fallback
 *
 * Props:
 *  - value: string (single URL) OR array of URLs (multi)
 *  - onChange: (newValue) => void
 *  - multiple: boolean
 *  - label: string
 *  - maxFiles: number (only with multiple)
 */
export default function ImageUploader({
  value,
  onChange,
  multiple = false,
  label = 'Качи изображение',
  maxFiles = 30,
}) {
  const [uploads, setUploads] = useState([]); // [{id, name, progress, status, error}]
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const list = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const validateFile = (file) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
      if (!ACCEPTED_EXTS.includes(ext)) {
        return `Неподдържан формат "${file.type || ext}". Разрешени: JPG, PNG, WebP`;
      }
    }
    if (file.size > MAX_FILE_BYTES) {
      const mb = (file.size / 1024 / 1024).toFixed(1);
      return `Файлът е твърде голям: ${mb}MB. Максимум: 12MB`;
    }
    return null;
  };

  const uploadOne = (file, uploadId) =>
    new Promise((resolve) => {
      const token = localStorage.getItem('auth_token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_URL}/api/media/upload`, true);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress } : u)));
        }
      };

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText || '{}');
          if (xhr.status >= 200 && xhr.status < 300 && data?.success) {
            const m = data.media || {};
            const url = m.url_medium || m.url_small || m.url_original;
            setUploads((prev) =>
              prev.map((u) => (u.id === uploadId ? { ...u, status: 'done', progress: 100 } : u))
            );
            resolve({ ok: true, url });
          } else {
            const err = data?.detail || `Грешка от сървъра (HTTP ${xhr.status})`;
            setUploads((prev) =>
              prev.map((u) => (u.id === uploadId ? { ...u, status: 'error', error: err } : u))
            );
            resolve({ ok: false, error: err });
          }
        } catch (e) {
          const err = `Невалиден отговор от сървъра (HTTP ${xhr.status})`;
          setUploads((prev) =>
            prev.map((u) => (u.id === uploadId ? { ...u, status: 'error', error: err } : u))
          );
          resolve({ ok: false, error: err });
        }
      };

      xhr.onerror = () => {
        const err = 'Мрежова грешка. Проверете връзката.';
        setUploads((prev) =>
          prev.map((u) => (u.id === uploadId ? { ...u, status: 'error', error: err } : u))
        );
        resolve({ ok: false, error: err });
      };

      xhr.ontimeout = () => {
        const err = 'Качването отне твърде дълго (timeout).';
        setUploads((prev) =>
          prev.map((u) => (u.id === uploadId ? { ...u, status: 'error', error: err } : u))
        );
        resolve({ ok: false, error: err });
      };

      xhr.timeout = 120000; // 2 min
      xhr.send(fd);
    });

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (files.length === 0) return;

      // Capacity for multi
      if (multiple) {
        const room = Math.max(0, maxFiles - list.length);
        if (room === 0) {
          toast.error(`Максимум ${maxFiles} файла`);
          return;
        }
        if (files.length > room) {
          toast.warning(`Качване само на първите ${room} файла`);
          files.splice(room);
        }
      }

      // Pre-validate
      const items = files.map((f) => {
        const err = validateFile(f);
        return {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: f.name,
          size: f.size,
          file: f,
          status: err ? 'error' : 'pending',
          progress: 0,
          error: err,
        };
      });

      setUploads((prev) => [...prev, ...items]);

      // Show client-side validation errors
      items.filter((i) => i.status === 'error').forEach((i) => toast.error(`${i.name}: ${i.error}`));

      const uploadedUrls = [];
      for (const item of items) {
        if (item.status === 'error') continue;
        setUploads((prev) => prev.map((u) => (u.id === item.id ? { ...u, status: 'uploading' } : u)));
        const res = await uploadOne(item.file, item.id);
        if (res.ok && res.url) {
          uploadedUrls.push(res.url);
        } else {
          toast.error(`${item.name}: ${res.error || 'Грешка'}`);
        }
      }

      if (uploadedUrls.length > 0) {
        if (multiple) {
          onChange([...list, ...uploadedUrls]);
        } else {
          onChange(uploadedUrls[uploadedUrls.length - 1]);
        }
        toast.success(`Качени ${uploadedUrls.length} ${uploadedUrls.length === 1 ? 'файл' : 'файла'}`);
      }

      // Cleanup completed entries after a delay
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.status === 'uploading' || u.status === 'pending'));
      }, 3500);

      if (inputRef.current) inputRef.current.value = '';
    },
    [list, multiple, maxFiles, onChange]
  );

  const removeAt = (idx) => {
    if (multiple) {
      const next = list.filter((_, i) => i !== idx);
      onChange(next);
    } else {
      onChange('');
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        multiple={multiple}
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="image-uploader-input"
      />

      {/* Existing previews */}
      {list.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {list.map((url, idx) => (
            <div
              key={`${url}-${idx}`}
              className="relative w-28 h-28 rounded-lg overflow-hidden border border-[#D4AF37]/20 bg-[#0B0B0B] group"
            >
              <img
                src={resolveUrl(url)}
                alt=""
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement.classList.add('broken-img');
                }}
              />
              <div className="absolute inset-0 broken-img-placeholder hidden items-center justify-center text-[10px] text-red-400 p-1 text-center">
                Грешка<br />при зареждане
              </div>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/80 hover:bg-red-500 flex items-center justify-center opacity-100 group-hover:opacity-100 transition-opacity"
                data-testid={`image-remove-${idx}`}
                aria-label="Премахни"
              >
                <X size={12} className="text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone / button */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-6 flex flex-col items-center justify-center text-center transition-colors ${
          dragOver
            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
            : 'border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5'
        }`}
        data-testid="image-upload-trigger"
      >
        <Upload size={28} className="text-[#D4AF37] mb-2" />
        <p className="text-sm text-[#F6F2E8]">{label}</p>
        <p className="text-xs text-[#A9A9A9] mt-1">
          Издърпайте файл тук или натиснете • JPG / PNG / WebP • До 12MB
        </p>
        {multiple && (
          <p className="text-xs text-[#A9A9A9] mt-0.5">Можете да качите няколко файла наведнъж</p>
        )}
      </div>

      {/* Active uploads */}
      {uploads.length > 0 && (
        <div className="space-y-2 mt-2">
          {uploads.map((u) => (
            <div
              key={u.id}
              className="bg-[#0B0B0B] border border-[#D4AF37]/15 rounded-lg p-3 text-xs"
              data-testid={`upload-row-${u.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="truncate text-[#F6F2E8] max-w-[60%]">{u.name}</span>
                <span className="flex items-center gap-1 text-[#A9A9A9]">
                  {u.status === 'uploading' && <Loader2 size={12} className="animate-spin" />}
                  {u.status === 'done' && <CheckCircle2 size={12} className="text-green-400" />}
                  {u.status === 'error' && <AlertCircle size={12} className="text-red-400" />}
                  <span>
                    {u.status === 'uploading' && `${u.progress}%`}
                    {u.status === 'done' && 'Готово'}
                    {u.status === 'error' && 'Грешка'}
                    {u.status === 'pending' && 'Изчакване...'}
                  </span>
                </span>
              </div>
              {(u.status === 'uploading' || u.status === 'done') && (
                <div className="h-1 bg-[#111] rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${u.status === 'done' ? 'bg-green-400' : 'bg-[#D4AF37]'}`}
                    style={{ width: `${u.progress}%` }}
                  />
                </div>
              )}
              {u.status === 'error' && u.error && (
                <p className="text-red-400 mt-1">{u.error}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
